/**
 * Cimet-qa style speaker dialogue chat under Mapped transcriptions.
 * Turns come from diarized word_timestamps / dialogue_turns on each snippet.
 */
import { useMemo, useState } from 'react';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { stampedAudioSrc } from '@/lib/reelsShortsAudioClean';

function fmtTs(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

function turnsFromSnippet(snip) {
  if (Array.isArray(snip?.dialogue_turns) && snip.dialogue_turns.length) {
    return snip.dialogue_turns;
  }
  const words = snip?.word_timestamps || [];
  const turns = [];
  for (const w of words) {
    if (!w || !(w.word || '').trim()) continue;
    const spk = String(w.speaker || w.speaker_label || 'spk_0');
    const start = w.start_ms != null ? Number(w.start_ms) / 1000 : Number(w.start || 0);
    const end = w.end_ms != null ? Number(w.end_ms) / 1000 : Number(w.end || start);
    if (!turns.length || turns[turns.length - 1].speaker !== spk) {
      turns.push({
        speaker: spk,
        side: spk === 'spk_1' || spk === 'right' ? 'right' : 'left',
        label: spk === 'spk_1' || spk === 'right' ? 'Speaker B' : 'Speaker A',
        words: [w],
        start,
        end,
        text: String(w.word || '').trim(),
      });
    } else {
      const t = turns[turns.length - 1];
      t.words.push(w);
      t.end = end;
      t.text = `${t.text} ${String(w.word || '').trim()}`.trim();
    }
  }
  return turns;
}

function trackUrlForSide(snip, side) {
  const tracks = snip?.speaker_audio_tracks || [];
  const hit = tracks.find((t) => String(t.side || '') === side);
  const url = hit?.s3_url || '';
  const base = mediaUrlForPlayback(url);
  if (!base) return '';
  return stampedAudioSrc(base, snip.updated_at || '');
}

export function ReelsShortsDialogueChat({ snippet }) {
  const turns = useMemo(() => turnsFromSnippet(snippet), [snippet]);
  const [activeSide, setActiveSide] = useState(null);

  if (!turns.length) {
    const dialogue = String(snippet?.transcript_dialogue || '').trim();
    if (dialogue) {
      return (
        <div className="space-y-2" data-testid="reels-dialogue-from-text">
          {dialogue.split('\n').map((line, idx) => (
            <p key={idx} className="text-[13px] text-slate-200 leading-relaxed">
              {line}
            </p>
          ))}
        </div>
      );
    }
    return (
      <p className="text-[12px] text-slate-500 py-3" data-testid="reels-dialogue-empty">
        No speaker turns yet — run Retry Split A/V after transcripts are ready.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="reels-dialogue-chat">
      <div className="flex items-center gap-4 text-[10px]">
        <span className="flex items-center gap-1.5 text-sky-300">
          <span className="w-2.5 h-2.5 rounded-full bg-sky-500 inline-block" />
          Speaker A
        </span>
        <span className="flex items-center gap-1.5 text-emerald-300">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
          Speaker B
        </span>
      </div>

      <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
        {turns.map((turn, idx) => {
          const isA = turn.side !== 'right' && turn.speaker !== 'spk_1';
          const side = isA ? 'left' : 'right';
          return (
            <div
              key={`${turn.start}-${idx}`}
              className={`flex gap-2 ${isA ? '' : 'flex-row-reverse'}`}
              data-testid={`reels-dialogue-turn-${idx}`}
            >
              <div
                className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white mt-0.5 ${
                  isA ? 'bg-sky-500' : 'bg-emerald-500'
                }`}
              >
                {isA ? 'A' : 'B'}
              </div>
              <div className={`max-w-[85%] ${isA ? '' : 'items-end'}`}>
                <p
                  className={`text-[10px] font-semibold mb-0.5 ${
                    isA ? 'text-sky-300' : 'text-emerald-300 text-right'
                  }`}
                >
                  {turn.label || (isA ? 'Speaker A' : 'Speaker B')}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveSide(side)}
                  className={`rounded-2xl px-3 py-2 text-left text-[13px] leading-relaxed w-full transition ${
                    isA
                      ? 'bg-sky-500/15 border border-sky-400/30 text-slate-100 rounded-tl-sm hover:border-sky-300/50'
                      : 'bg-emerald-500/15 border border-emerald-400/30 text-slate-100 rounded-tr-sm hover:border-emerald-300/50'
                  }`}
                >
                  {turn.text}
                </button>
                <p className={`text-[10px] text-slate-500 mt-0.5 ${isA ? '' : 'text-right'}`}>
                  {fmtTs(turn.start)}
                  {turn.end != null ? ` – ${fmtTs(turn.end)}` : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {activeSide ? (
        <div className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-2 space-y-1">
          <p className="text-[10px] uppercase tracking-widest text-slate-400">
            Isolated · {activeSide === 'left' ? 'Speaker A' : 'Speaker B'}
          </p>
          {trackUrlForSide(snippet, activeSide) ? (
            <audio
              key={activeSide}
              controls
              autoPlay
              preload="metadata"
              className="w-full h-8"
              src={trackUrlForSide(snippet, activeSide)}
            />
          ) : (
            <p className="text-[11px] text-slate-500">No isolated track for this speaker yet.</p>
          )}
        </div>
      ) : null}

      <p className="text-[10px] text-slate-500">
        {turns.length} dialogue turn{turns.length === 1 ? '' : 's'} · tap a bubble to hear that speaker&apos;s isolate
      </p>
    </div>
  );
}

export default ReelsShortsDialogueChat;
